// GitHub API Service for fetching repository content
interface GitHubFile {
  name: string;
  path: string;
  sha: string;
  size: number;
  url: string;
  html_url: string;
  git_url: string;
  download_url: string | null;
  type: 'file' | 'dir';
  content?: string;
  encoding?: string;
}

interface GitHubRepository {
  id: number;
  name: string;
  full_name: string;
  description: string | null;
  private: boolean;
  html_url: string;
  default_branch: string;
  branches_url: string;
}

interface GitHubBranch {
  name: string;
  commit: {
    sha: string;
    url: string;
  };
  protected: boolean;
}

// New interfaces for Pull Request functionality
interface GitHubPullRequest {
  id: number;
  number: number;
  state: 'open' | 'closed' | 'merged';
  title: string;
  body: string;
  html_url: string;
  diff_url: string;
  patch_url: string;
  issue_url: string;
  commits_url: string;
  review_comments_url: string;
  review_comment_url: string;
  comments_url: string;
  statuses_url: string;
  head: {
    label: string;
    ref: string;
    sha: string;
    user: {
      login: string;
      id: number;
      avatar_url: string;
      html_url: string;
    };
    repo: {
      id: number;
      name: string;
      full_name: string;
      private: boolean;
      html_url: string;
    };
  };
  base: {
    label: string;
    ref: string;
    sha: string;
    user: {
      login: string;
      id: number;
      avatar_url: string;
      html_url: string;
    };
    repo: {
      id: number;
      name: string;
      full_name: string;
      private: boolean;
      html_url: string;
    };
  };
  user: {
    login: string;
    id: number;
    avatar_url: string;
    html_url: string;
  };
  assignees: Array<{
    login: string;
    id: number;
    avatar_url: string;
    html_url: string;
  }>;
  requested_reviewers: Array<{
    login: string;
    id: number;
    avatar_url: string;
    html_url: string;
  }>;
  labels: Array<{
    id: number;
    name: string;
    color: string;
    description: string;
  }>;
  draft: boolean;
  merged: boolean;
  mergeable: boolean;
  mergeable_state: 'clean' | 'dirty' | 'unstable' | 'blocked';
  merged_by: {
    login: string;
    id: number;
    avatar_url: string;
    html_url: string;
  } | null;
  merge_commit_sha: string | null;
  comments: number;
  review_comments: number;
  commits: number;
  additions: number;
  deletions: number;
  changed_files: number;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  merged_at: string | null;
}

interface GitHubPRStatus {
  state: 'pending' | 'success' | 'failure' | 'error';
  target_url: string;
  description: string;
  context: string;
  created_at: string;
  updated_at: string;
}

interface GitHubPRReview {
  id: number;
  user: {
    login: string;
    id: number;
    avatar_url: string;
    html_url: string;
  };
  body: string;
  state: 'APPROVED' | 'CHANGES_REQUESTED' | 'COMMENTED' | 'DISMISSED' | 'PENDING';
  html_url: string;
  pull_request_url: string;
  submitted_at: string;
}

interface GitHubRateLimit {
  limit: number;
  remaining: number;
  reset: number;
  used: number;
}

class GitHubService {
  private static readonly GITHUB_API_BASE = 'https://api.github.com';
  private static readonly MAX_RETRIES = 3;
  private static readonly BASE_DELAY = 1000; // 1 second

  /**
   * Get GitHub token from localStorage
   */
  private static getGitHubToken(): string | null {
    const settings = localStorage.getItem('appSettings');
    console.log('Reading settings from localStorage:', settings ? 'Settings found' : 'No settings');
    
    if (settings) {
      try {
        const parsed = JSON.parse(settings);
        console.log('Parsed settings:', parsed);
        const token = parsed.github?.token || null;
        console.log('GitHub token found:', token ? 'Token present' : 'No token');
        return token;
      } catch (error) {
        console.error('Failed to parse app settings:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Prefer a non-empty project-scoped token and fall back to stored token.
   */
  private static getGitHubTokenForRequest(projectScopedToken?: string | null): string | null {
    if (typeof projectScopedToken === 'string') {
      const token = projectScopedToken.trim();
      if (token !== '') return token;
    }
    return this.getGitHubToken();
  }

  /**
   * Get headers for GitHub API requests
   */
  private static getHeaders(projectScopedToken?: string | null): HeadersInit {
    const token = this.getGitHubTokenForRequest(projectScopedToken);
    const headers: HeadersInit = {
      'Accept': 'application/vnd.github.v3+json',
      'User-Agent': 'VishMaker-App'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('Adding Authorization header with token:', `Bearer ${token.substring(0, 10)}...`);
      console.log('Full headers being sent:', headers);
    } else {
      console.log('No token found, making unauthenticated request');
    }
    
    return headers;
  }

  /**
   * Get headers for unauthenticated requests (fallback)
   */
  private static getUnauthenticatedHeaders(): HeadersInit {
    return {
      'Accept': 'application/vnd.github.v3+json',
      'User-Agent': 'VishMaker-App'
    };
  }

  /**
   * Check if a response indicates token authentication failure
   */
  private static isTokenAuthFailure(response: Response): boolean {
    // 401 Unauthorized or 403 Forbidden with specific error messages
    if (response.status === 401) return true;
    if (response.status === 403) {
      const rateLimitRemaining = response.headers.get('x-ratelimit-remaining');
      // If we have remaining rate limit but still get 403, it's likely an auth issue
      if (rateLimitRemaining && rateLimitRemaining !== '0') {
        return true;
      }
    }
    return false;
  }

  /**
   * Make a request with fallback to unauthenticated if token fails
   */
  private static async makeRequestWithFallback<T>(
    url: string,
    options: RequestInit = {},
    projectScopedToken?: string | null
  ): Promise<{ data: T; authenticated: boolean; fallbackUsed: boolean }> {
    const token = this.getGitHubTokenForRequest(projectScopedToken);
    
    if (token) {
      try {
        // Try with token first
        const response = await fetch(url, {
          ...options,
          headers: { ...this.getHeaders(projectScopedToken), ...options.headers }
        });
        
        if (response.ok) {
          const data = await response.json();
          return { data, authenticated: true, fallbackUsed: false };
        }
        
        // Check if it's a token authentication failure
        if (this.isTokenAuthFailure(response)) {
          console.log('Token authentication failed, falling back to unauthenticated request');
          
          // Fall back to unauthenticated request
          const fallbackResponse = await fetch(url, {
            ...options,
            headers: { ...this.getUnauthenticatedHeaders(), ...options.headers }
          });
          
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            return { data: fallbackData, authenticated: false, fallbackUsed: true };
          } else {
            // Even unauthenticated failed, throw the original error
            throw new Error(`Request failed: ${response.statusText} (${response.status})`);
          }
        } else {
          // Other error, throw it
          throw new Error(`Request failed: ${response.statusText} (${response.status})`);
        }
      } catch (error) {
        console.log('Token request failed, falling back to unauthenticated request');
        
        // Fall back to unauthenticated request
        try {
          const fallbackResponse = await fetch(url, {
            ...options,
            headers: { ...this.getUnauthenticatedHeaders(), ...options.headers }
          });
          
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            return { data: fallbackData, authenticated: false, fallbackUsed: true };
          } else {
            throw new Error(`Fallback request failed: ${fallbackResponse.statusText} (${fallbackResponse.status})`);
          }
        } catch (fallbackError) {
          // Both failed, throw the original error
          throw error;
        }
      }
    } else {
      // No token, make unauthenticated request directly
      const response = await fetch(url, {
        ...options,
        headers: { ...this.getUnauthenticatedHeaders(), ...options.headers }
      });
      
      if (!response.ok) {
        throw new Error(`Request failed: ${response.statusText} (${response.status})`);
      }
      
      const data = await response.json();
      return { data, authenticated: false, fallbackUsed: false };
    }
  }

  /**
   * Check rate limits and throw appropriate error if exceeded
   */
  private static checkRateLimit(response: Response): void {
    const rateLimitRemaining = response.headers.get('x-ratelimit-remaining');
    const rateLimitReset = response.headers.get('x-ratelimit-reset');
    
    if (rateLimitRemaining === '0') {
      const resetTime = rateLimitReset ? new Date(parseInt(rateLimitReset) * 1000) : null;
      const resetTimeStr = resetTime ? resetTime.toLocaleString() : 'unknown time';
      
      throw new Error(
        `GitHub API rate limit exceeded. Rate limit resets at ${resetTimeStr}. ` +
        `Please try again later or use a GitHub token for higher limits.`
      );
    }
  }

  /**
   * Sleep for a given number of milliseconds
   */
  private static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Retry a function with exponential backoff
   */
  private static async retryWithBackoff<T>(
    fn: () => Promise<T>,
    retries: number = this.MAX_RETRIES
  ): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      if (retries <= 0) {
        throw error;
      }
      
      const delay = this.BASE_DELAY * Math.pow(2, this.MAX_RETRIES - retries);
      console.log(`Retrying in ${delay}ms... (${retries} retries left)`);
      await this.sleep(delay);
      
      return this.retryWithBackoff(fn, retries - 1);
    }
  }

  /**
   * Test GitHub connection and authentication
   */
  static async testConnection(): Promise<{ 
    authenticated: boolean; 
    rateLimit: GitHubRateLimit;
    user?: string;
    error?: string;
    fallbackUsed?: boolean;
  }> {
    try {
      // Try to get rate limit info with fallback
      const rateLimitResult = await this.makeRequestWithFallback<{ resources: { core: GitHubRateLimit } }>(
        `${this.GITHUB_API_BASE}/rate_limit`
      );
      
      const rateLimit = rateLimitResult.data.resources.core;
      const authenticated = rateLimitResult.authenticated;
      
      let user: string | undefined;
      if (authenticated) {
        try {
          const userResult = await this.makeRequestWithFallback<{ login: string }>(
            `${this.GITHUB_API_BASE}/user`
          );
          if (userResult.authenticated) {
            user = userResult.data.login;
          }
        } catch (userError) {
          console.warn('Could not fetch user info:', userError);
        }
      }
      
      console.log(`GitHub connection test:`, {
        authenticated,
        rateLimit,
        user,
        resetTime: new Date(rateLimit.reset * 1000).toLocaleString(),
        fallbackUsed: rateLimitResult.fallbackUsed
      });
      
      return { 
        authenticated, 
        rateLimit,
        user,
        error: undefined,
        fallbackUsed: rateLimitResult.fallbackUsed
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error testing GitHub connection:', error);
      return { 
        authenticated: false, 
        rateLimit: { limit: 0, remaining: 0, reset: 0, used: 0 },
        error: errorMessage,
        fallbackUsed: false
      };
    }
  }

  /**
   * Check if we can access a specific repository
   */
  static async checkRepositoryAccess(
    owner: string,
    repo: string,
    projectScopedToken?: string | null
  ): Promise<{
    accessible: boolean;
    private: boolean;
    error?: string;
    fallbackUsed?: boolean;
  }> {
    try {
      const result = await this.makeRequestWithFallback<GitHubRepository>(
        `${this.GITHUB_API_BASE}/repos/${owner}/${repo}`,
        {},
        projectScopedToken
      );
      
      return {
        accessible: true,
        private: result.data.private,
        error: undefined,
        fallbackUsed: result.fallbackUsed
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Check if it's a 404 (repo not found) vs other errors
      if (errorMessage.includes('404')) {
        return {
          accessible: false,
          private: false,
          error: `Repository '${owner}/${repo}' not found. Please check the repository name and ensure it exists.`,
          fallbackUsed: false
        };
      } else {
        return {
          accessible: false,
          private: true,
          error: `Repository '${owner}/${repo}' is private or requires authentication. Please check repository permissions.`,
          fallbackUsed: false
        };
      }
    }
  }

  /**
   * Fetch repository information
   */
  static async getRepository(
    owner: string,
    repo: string,
    projectScopedToken?: string | null
  ): Promise<GitHubRepository> {
    return this.retryWithBackoff(async () => {
      // First check if we can access the repository
      const accessCheck = await this.checkRepositoryAccess(owner, repo, projectScopedToken);
      if (!accessCheck.accessible) {
        throw new Error(accessCheck.error || 'Repository access denied');
      }

      const response = await fetch(`${this.GITHUB_API_BASE}/repos/${owner}/${repo}`, {
        headers: this.getHeaders(projectScopedToken)
      });
      
      if (!response.ok) {
        this.checkRateLimit(response);
        throw new Error(`Failed to fetch repository: ${response.statusText} (${response.status})`);
      }
      
      return await response.json();
    });
  }

  /**
   * Fetch repository branches
   */
  static async getBranches(
    owner: string,
    repo: string,
    projectScopedToken?: string | null
  ): Promise<GitHubBranch[]> {
    return this.retryWithBackoff(async () => {
      const response = await fetch(`${this.GITHUB_API_BASE}/repos/${owner}/${repo}/branches`, {
        headers: this.getHeaders(projectScopedToken)
      });
      
      if (!response.ok) {
        this.checkRateLimit(response);
        if (response.status === 404) {
          throw new Error(`Repository '${owner}/${repo}' not found.`);
        } else if (response.status === 403) {
          throw new Error(`Repository '${owner}/${repo}' is private or requires authentication. Please check repository permissions.`);
        } else {
          throw new Error(`Failed to fetch branches: ${response.statusText} (${response.status})`);
        }
      }
      
      return await response.json();
    });
  }

  /**
   * Fetch repository contents (files and directories)
   */
  static async getContents(
    owner: string,
    repo: string,
    path: string = '',
    branch: string = 'main',
    projectScopedToken?: string | null
  ): Promise<GitHubFile[]> {
    return this.retryWithBackoff(async () => {
      const url = `${this.GITHUB_API_BASE}/repos/${owner}/${repo}/contents/${path}?ref=${branch}`;
      console.log(`Fetching GitHub contents from: ${url}`);
      
      const result = await this.makeRequestWithFallback<GitHubFile[] | GitHubFile>(
        url,
        {},
        projectScopedToken
      );
      
      console.log(`GitHub API response - Authenticated: ${result.authenticated}, Fallback used: ${result.fallbackUsed}`);
      
      const data = result.data;
      console.log(`Successfully fetched ${Array.isArray(data) ? data.length : 1} items from GitHub`);
      return Array.isArray(data) ? data : [data];
    });
  }

  /**
   * Fetch a single file's content with authentication and fallback
   */
  static async getFileContent(
    owner: string,
    repo: string,
    path: string,
    branch: string = 'main',
    projectScopedToken?: string | null
  ): Promise<string> {
    return this.retryWithBackoff(async () => {
      const url = `${this.GITHUB_API_BASE}/repos/${owner}/${repo}/contents/${path}?ref=${branch}`;
      console.log(`Fetching file content from: ${url}`);
      
      const result = await this.makeRequestWithFallback<{ content: string; encoding: string; type: string }>(
        url,
        {},
        projectScopedToken
      );
      console.log(`File API response - Authenticated: ${result.authenticated}, Fallback used: ${result.fallbackUsed}`);
      
      const data = result.data;
      
      if (data.type !== 'file') {
        throw new Error('Path does not point to a file');
      }
      
      // Decode content from base64
      const content = atob(data.content);
      return content;
    });
  }

  /**
   * Get raw file content using GitHub API
   */
  static async getRawFileContent(
    owner: string,
    repo: string,
    path: string,
    branch: string = 'main',
    projectScopedToken?: string | null
  ): Promise<string> {
    return this.retryWithBackoff(async () => {
      const url = `${this.GITHUB_API_BASE}/repos/${owner}/${repo}/contents/${path}?ref=${branch}`;
      console.log(`Fetching file content from: ${url}`);
      
      const result = await this.makeRequestWithFallback<{ content: string; encoding: string }>(
        url,
        {},
        projectScopedToken
      );
      console.log(`File API response - Authenticated: ${result.authenticated}, Fallback used: ${result.fallbackUsed}`);
      
      const data = result.data;
      
      // Base64 decode the content
      const content = atob(data.content);
      console.log(`Successfully fetched file content (${content.length} characters)`);
      return content;
    });
  }

  /**
   * Parse repository path into owner and repo
   */
  static parseRepoPath(repoPath: string): { owner: string; repo: string } | null {
    const match = repoPath.match(/^([^\/]+)\/([^\/]+)$/);
    if (!match) {
      return null;
    }
    return {
      owner: match[1],
      repo: match[2]
    };
  }

  /**
   * Get file extension for syntax highlighting
   */
  static getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Check if file is code file based on extension
   */
  static isCodeFile(filename: string): boolean {
    const codeExtensions = [
      'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'cs', 'php', 'rb', 'go', 'rs',
      'swift', 'kt', 'scala', 'html', 'css', 'scss', 'sass', 'less', 'json', 'xml', 'yaml',
      'yml', 'md', 'txt', 'sh', 'bash', 'zsh', 'fish', 'sql', 'vue', 'svelte', 'astro'
    ];
    const ext = this.getFileExtension(filename);
    return codeExtensions.includes(ext);
  }

  /**
   * Get syntax highlighting language based on file extension
   */
  static getLanguageFromExtension(filename: string): string {
    const ext = this.getFileExtension(filename);
    const languageMap: Record<string, string> = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c',
      'cs': 'csharp',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'swift': 'swift',
      'kt': 'kotlin',
      'scala': 'scala',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'sass': 'sass',
      'less': 'less',
      'json': 'json',
      'xml': 'xml',
      'yaml': 'yaml',
      'yml': 'yaml',
      'md': 'markdown',
      'txt': 'text',
      'sh': 'bash',
      'bash': 'bash',
      'zsh': 'bash',
      'fish': 'bash',
      'sql': 'sql',
      'vue': 'vue',
      'svelte': 'svelte',
      'astro': 'astro'
    };
    return languageMap[ext] || 'text';
  }

  /**
   * Get suggested public repositories for testing
   */
  static getSuggestedRepositories(): Array<{ name: string; description: string }> {
    return [
      { name: 'facebook/react', description: 'React JavaScript library' },
      { name: 'microsoft/vscode', description: 'Visual Studio Code editor' },
      { name: 'vuejs/vue', description: 'Vue.js framework' },
      { name: 'angular/angular', description: 'Angular framework' },
      { name: 'nodejs/node', description: 'Node.js runtime' },
      { name: 'python/cpython', description: 'Python programming language' },
      { name: 'torvalds/linux', description: 'Linux kernel' },
      { name: 'microsoft/TypeScript', description: 'TypeScript language' }
    ];
  }

  /**
   * Extract owner, repo, and PR number from a GitHub PR URL
   */
  static parsePRUrl(prUrl: string): { owner: string; repo: string; prNumber: number } | null {
    const match = prUrl.match(/github\.com\/([^\/]+)\/([^\/]+)\/pull\/(\d+)/);
    if (!match) {
      return null;
    }
    return {
      owner: match[1],
      repo: match[2],
      prNumber: parseInt(match[3], 10)
    };
  }

  /**
   * Get Pull Request information
   */
  static async getPullRequest(owner: string, repo: string, prNumber: number): Promise<GitHubPullRequest> {
    return this.retryWithBackoff(async () => {
      const response = await fetch(`${this.GITHUB_API_BASE}/repos/${owner}/${repo}/pulls/${prNumber}`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        this.checkRateLimit(response);
        if (response.status === 404) {
          throw new Error(`Pull Request #${prNumber} not found in repository '${owner}/${repo}'.`);
        } else if (response.status === 403) {
          throw new Error(`Repository '${owner}/${repo}' is private or requires authentication.`);
        } else {
          throw new Error(`Failed to fetch pull request: ${response.statusText} (${response.status})`);
        }
      }
      
      return await response.json();
    });
  }

  /**
   * Get Pull Request status checks
   */
  static async getPullRequestStatus(owner: string, repo: string, prNumber: number): Promise<GitHubPRStatus[]> {
    return this.retryWithBackoff(async () => {
      const response = await fetch(`${this.GITHUB_API_BASE}/repos/${owner}/${repo}/commits/${prNumber}/status`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        this.checkRateLimit(response);
        if (response.status === 404) {
          throw new Error(`Status not found for PR #${prNumber} in repository '${owner}/${repo}'.`);
        } else if (response.status === 403) {
          throw new Error(`Repository '${owner}/${repo}' is private or requires authentication.`);
        } else {
          throw new Error(`Failed to fetch PR status: ${response.statusText} (${response.status})`);
        }
      }
      
      const data = await response.json();
      return data.statuses || [];
    });
  }

  /**
   * Get Pull Request reviews
   */
  static async getPullRequestReviews(owner: string, repo: string, prNumber: number): Promise<GitHubPRReview[]> {
    return this.retryWithBackoff(async () => {
      const response = await fetch(`${this.GITHUB_API_BASE}/repos/${owner}/${repo}/pulls/${prNumber}/reviews`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        this.checkRateLimit(response);
        if (response.status === 404) {
          throw new Error(`Reviews not found for PR #${prNumber} in repository '${owner}/${repo}'.`);
        } else if (response.status === 403) {
          throw new Error(`Repository '${owner}/${repo}' is private or requires authentication.`);
        } else {
          throw new Error(`Failed to fetch PR reviews: ${response.statusText} (${response.status})`);
        }
      }
      
      return await response.json();
    });
  }

  /**
   * Check if a Pull Request is approved and ready for merge
   */
  static async isPullRequestApproved(owner: string, repo: string, prNumber: number): Promise<{
    approved: boolean;
    reviewCount: number;
    approvalCount: number;
    changesRequested: boolean;
    lastReviewState: string;
    prState: string;
    mergeable: boolean;
  }> {
    try {
      const [pr, reviews] = await Promise.all([
        this.getPullRequest(owner, repo, prNumber),
        this.getPullRequestReviews(owner, repo, prNumber)
      ]);

      const approvalCount = reviews.filter(review => review.state === 'APPROVED').length;
      const changesRequested = reviews.some(review => review.state === 'CHANGES_REQUESTED');
      const lastReview = reviews.length > 0 ? reviews[reviews.length - 1] : null;

      return {
        approved: approvalCount > 0 && !changesRequested && pr.state === 'open',
        reviewCount: reviews.length,
        approvalCount,
        changesRequested,
        lastReviewState: lastReview?.state || 'NO_REVIEWS',
        prState: pr.state,
        mergeable: pr.mergeable && pr.mergeable_state === 'clean'
      };
    } catch (error) {
      console.error('Error checking PR approval status:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive PR information including status and reviews
   */
  static async getPullRequestInfo(prUrl: string): Promise<{
    pr: GitHubPullRequest;
    status: GitHubPRStatus[];
    reviews: GitHubPRReview[];
    approvalInfo: {
      approved: boolean;
      reviewCount: number;
      approvalCount: number;
      changesRequested: boolean;
      lastReviewState: string;
      prState: string;
      mergeable: boolean;
    };
  } | null> {
    try {
      const parsed = this.parsePRUrl(prUrl);
      if (!parsed) {
        throw new Error('Invalid GitHub PR URL format');
      }

      const { owner, repo, prNumber } = parsed;
      
      const [pr, status, reviews] = await Promise.all([
        this.getPullRequest(owner, repo, prNumber),
        this.getPullRequestStatus(owner, repo, prNumber),
        this.getPullRequestReviews(owner, repo, prNumber)
      ]);

      const approvalInfo = await this.isPullRequestApproved(owner, repo, prNumber);

      return {
        pr,
        status,
        reviews,
        approvalInfo
      };
    } catch (error) {
      console.error('Error getting comprehensive PR info:', error);
      return null;
    }
  }

  /**
   * Test the fallback functionality
   */
  static async testFallback(): Promise<void> {
    console.log('Testing GitHub Service Fallback Functionality');
    console.log('============================================');
    
    try {
      // Test connection with fallback
      console.log('\n1. Testing connection with fallback...');
      const connection = await this.testConnection();
      console.log('Connection result:', {
        authenticated: connection.authenticated,
        rateLimit: connection.rateLimit,
        user: connection.user,
        fallbackUsed: connection.fallbackUsed
      });
      
      // Test repository access with fallback
      console.log('\n2. Testing repository access with fallback...');
      const testRepo = 'spicechemistryaustin/spicechemistry-authentication';
      const [owner, repo] = testRepo.split('/');
      
      const repoAccess = await this.checkRepositoryAccess(owner, repo);
      console.log('Repository access result:', {
        accessible: repoAccess.accessible,
        private: repoAccess.private,
        fallbackUsed: repoAccess.fallbackUsed,
        error: repoAccess.error
      });
      
      if (repoAccess.accessible) {
        console.log('\n3. Testing content fetch with fallback...');
        try {
          const contents = await this.getContents(owner, repo, '', 'main');
          console.log('Successfully fetched contents:', contents.length, 'items');
        } catch (contentError) {
          console.log('Content fetch failed:', contentError);
        }
      }
      
    } catch (error) {
      console.error('Fallback test failed:', error);
    }
    
    console.log('\nFallback test complete!');
  }
}

export default GitHubService;
export type { GitHubFile, GitHubRepository, GitHubBranch, GitHubPullRequest, GitHubPRStatus, GitHubPRReview, GitHubRateLimit }; 